/**
 * SimplyLoan SEO engine API client (lambda_extract/seo_engine/routes.py).
 *
 * Every route lives under /api/simplyloan/seo. Reads take ?market=SE|NO; writes are POST with a JSON
 * body and need a signed-in user (the Cognito token is attached by ../authFetch).
 */
import { API_BASE_URL } from "../config";
import type {
  ContentItem,
  GenerateResult,
  IndexingResult,
  InspectResult,
  KeywordItem,
  PusherSettings,
  PusherState,
  QualityReport,
  RadarData,
  SeoAlert,
  SeoAuthor,
  SeoContentDetail,
  SeoMarket,
  SeoOverview,
  TransitionResult,
} from "../pages/seo/types";

const BASE = `${API_BASE_URL}/api/simplyloan/seo`;

export class SeoApiError extends Error {
  status: number;
  payload: any;

  constructor(message: string, status: number, payload: any) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  let data: any = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    throw new SeoApiError(data?.error || `HTTP ${res.status}`, res.status, data);
  }
  return data as T;
}

const get = <T>(path: string, market?: SeoMarket) =>
  request<T>(market ? `${path}${path.includes("?") ? "&" : "?"}market=${market}` : path);

const post = <T>(path: string, body: Record<string, unknown>) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body) });

export const seoApi = {
  overview: (market: SeoMarket) => get<SeoOverview>("/overview", market),

  content: (market: SeoMarket) => get<{ items: ContentItem[] }>("/content", market),
  contentDetail: (market: SeoMarket, id: string) => get<SeoContentDetail>(`/content/${id}`, market),
  createContent: (market: SeoMarket, payload: Record<string, unknown>) =>
    post<{ id: number }>("/content", { market, ...payload }),
  updateContent: (market: SeoMarket, id: string, payload: Record<string, unknown>) =>
    post<{ ok: boolean }>(`/content/${id}`, { market, ...payload }),
  transition: (market: SeoMarket, id: string, to: string, opts: { factChecked?: boolean; note?: string; version?: number } = {}) =>
    post<TransitionResult>(`/content/${id}/transition`, { market, to, ...opts }),
  qualityCheck: (market: SeoMarket, id: string) => post<QualityReport>(`/content/${id}/quality-check`, { market }),
  generate: (market: SeoMarket, payload: { mode: "auto" | "news" | "evergreen"; keyword?: string; contentId?: string }) =>
    post<GenerateResult>("/content/generate", { market, ...payload }),

  keywords: (market: SeoMarket) => get<{ items: KeywordItem[] }>("/keywords", market),
  addKeywords: (market: SeoMarket, keywords: string[], extra: { intent?: string; targetUrl?: string } = {}) =>
    post<{ added: number }>("/keywords", { market, keywords, ...extra }),
  cannibalization: (market: SeoMarket) => get<{ items: Array<Record<string, any>> }>("/cannibalization", market),

  competitors: (market: SeoMarket) => get<RadarData>("/competitors", market),
  addCompetitor: (market: SeoMarket, payload: { name: string; domain: string; category?: string; trackBacklinks?: boolean }) =>
    post<{ id: number }>("/competitors", { market, ...payload }),
  addWatchPage: (market: SeoMarket, competitorId: number, url: string, pageType = "comparison") =>
    post<{ id: number }>("/competitors/watch-pages", { market, competitorId, url, pageType }),

  alerts: (market: SeoMarket) => get<{ items: SeoAlert[] }>("/alerts", market),
  ackAlert: (market: SeoMarket, id: number, resolve = false) => post<{ ok: boolean }>(`/alerts/${id}/ack`, { market, resolve }),

  pusher: (market: SeoMarket) => get<PusherState>("/pusher", market),
  savePusherSettings: (market: SeoMarket, settings: Partial<PusherSettings>) =>
    post<{ ok: boolean; settings: PusherSettings }>("/pusher/settings", { market, settings }),
  triggerPush: (market: SeoMarket, contentId?: string) =>
    post<{ status: string; url?: string; message?: string; blocking?: string[]; deploy?: Record<string, any> }>(
      "/pusher/trigger", { market, ...(contentId ? { contentId } : {}) }),
  replan: (market: SeoMarket) => post<Record<string, number>>("/pusher/replan", { market }),

  submitUrl: (market: SeoMarket, target: { url?: string; contentId?: string }) =>
    post<IndexingResult>("/indexing/submit", { market, ...target }),
  inspectUrl: (market: SeoMarket, target: { url?: string; contentId?: string }) =>
    post<InspectResult>("/indexing/inspect", { market, ...target }),

  authors: () => get<{ items: SeoAuthor[] }>("/authors"),
  saveAuthor: (author: Partial<SeoAuthor> & { name: string; jobTitle: string }) =>
    post<{ id: number; slug: string }>("/authors", author as Record<string, unknown>),

  roadmap: () =>
    get<{ items: Array<{ task_id: string; status: string; status_note?: string; due_date?: string; updated_at?: string }> }>("/roadmap"),
  updateRoadmapTask: (taskId: string, status: "done" | "needs_you" | "planned" | "auto", note?: string) =>
    post<{ ok: boolean; taskId: string; status: string }>("/roadmap", { taskId, status, ...(note ? { note } : {}) }),
};
